import React, { useEffect, useMemo, useRef, useState } from 'react';
import { employeeAttachmentsAPI, employeeFoldersAPI, employeeLicensesAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Folder, FolderPlus, FolderOpen, FileText, Upload, Download, Trash2, Pencil, Lock, ChevronRight,
} from 'lucide-react';
import { formatDate, cn } from '../lib/utils';

/**
 * Hierarchical folder tree + attachments table with drag-drop upload.
 * - Root view shows folders + files at the root.
 * - Click a folder to drill in. Breadcrumbs allow navigating back up.
 * - Drag-drop a file anywhere inside the panel to upload into the current folder.
 * - Only admin + HR may create / rename / delete folders.
 */
export function AttachmentsManager({ employeeId }) {
  const { user } = useAuth();
  const canManageFolders = ['admin', 'hr'].includes(user?.role);

  const [folders, setFolders] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState([]); // array of folder ids representing breadcrumb
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => { load(); }, [employeeId]);

  const load = async () => {
    setLoading(true);
    try {
      const [foldersRes, attsRes] = await Promise.all([
        employeeFoldersAPI.list(employeeId),
        employeeAttachmentsAPI.list(employeeId),
      ]);
      setFolders(foldersRes.data || []);
      setAttachments(attsRes.data || []);
    } catch (e) {
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const currentFolderId = path.length ? path[path.length - 1] : null;
  const currentFolder = folders.find(f => f.id === currentFolderId);
  const inLicensingFolder = !!currentFolder?.is_licensing;

  const childFolders = useMemo(
    () => folders
      .filter(f => (f.parent_id || null) === currentFolderId)
      .sort((a, b) => {
        // Reserved folders first, then alphabetical
        if (a.is_reserved && !b.is_reserved) return -1;
        if (!a.is_reserved && b.is_reserved) return 1;
        return a.name.localeCompare(b.name);
      }),
    [folders, currentFolderId]
  );

  const childAttachments = useMemo(
    () => attachments.filter(a => (a.folder_id || null) === currentFolderId),
    [attachments, currentFolderId]
  );

  const breadcrumb = useMemo(() => {
    const out = [{ id: null, name: 'All Files' }];
    for (const fid of path) {
      const f = folders.find(x => x.id === fid);
      if (f) out.push({ id: f.id, name: f.name, is_reserved: f.is_reserved });
    }
    return out;
  }, [path, folders]);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (currentFolderId) fd.append('folder_id', currentFolderId);
        await employeeAttachmentsAPI.upload(employeeId, fd);
        uploaded += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded) {
      toast.success(`${uploaded} file${uploaded === 1 ? '' : 's'} uploaded`);
      load();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (inLicensingFolder) return; // read-only reserved folder
    if (e.dataTransfer?.files?.length) uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Enter a folder name'); return; }
    try {
      await employeeFoldersAPI.create(employeeId, { name, parent_id: currentFolderId });
      toast.success('Folder created');
      setShowNewFolder(false);
      setNewFolderName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create folder');
    }
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name) { toast.error('Enter a folder name'); return; }
    try {
      await employeeFoldersAPI.rename(employeeId, renameTarget.id, name);
      toast.success('Folder renamed');
      setRenameTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rename folder');
    }
  };

  const handleDeleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}" and ALL its contents? This cannot be undone.`)) return;
    try {
      await employeeFoldersAPI.delete(employeeId, f.id);
      toast.success('Folder deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete folder');
    }
  };

  const handleDownload = async (att) => {
    try {
      if (att.licensing_ref) {
        const url = att.licensing_ref.kind === 'firearms'
          ? employeeLicensesAPI.firearmsQualificationFileUrl(employeeId, att.licensing_ref.id)
          : employeeLicensesAPI.licenseCredentialFileUrl(employeeId, att.licensing_ref.id);
        const res = await api.get(url, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/octet-stream' }));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = att.filename; a.click();
        window.URL.revokeObjectURL(blobUrl);
        return;
      }
      const res = await employeeAttachmentsAPI.download(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? 'You do not have access to this credential file.'
        : 'Download failed');
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"? This cannot be undone.`)) return;
    try {
      await employeeAttachmentsAPI.delete(att.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid="attachments-manager">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Attachments</CardTitle>
          <CardDescription>Documents filed against this employee. Drag files anywhere on this panel to upload.</CardDescription>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canManageFolders && (
            <Button variant="outline" onClick={() => setShowNewFolder(true)} data-testid="new-folder-btn">
              <FolderPlus className="w-4 h-4 mr-2" /> New Folder
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            data-testid="attachments-file-input"
          />
          {!inLicensingFolder && (
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-files-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm text-slate-600 mb-3 flex-wrap" data-testid="attachments-breadcrumb">
          {breadcrumb.map((b, idx) => (
            <React.Fragment key={`${b.id || 'root'}-${idx}`}>
              {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              <button
                onClick={() => setPath(path.slice(0, idx))}
                className={cn('hover:underline', idx === breadcrumb.length - 1 ? 'font-semibold text-slate-900' : '')}
              >
                {b.name}
                {b.is_reserved && <Lock className="w-3 h-3 inline ml-1 text-amber-600" />}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Drop zone wrapping everything */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={cn(
            'rounded-lg border-2 border-dashed transition-colors p-4',
            inLicensingFolder ? 'border-amber-200 bg-amber-50/40'
              : dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50/40'
          )}
          data-testid="attachments-dropzone"
        >
          {inLicensingFolder && (
            <div className="flex items-center gap-2 text-xs text-amber-800 mb-3" data-testid="licensing-readonly-note">
              <Lock className="w-3.5 h-3.5" />
              System-maintained folder. Verified credential files are referenced here automatically and cannot be added, renamed or deleted.
            </div>
          )}
          {loading ? (
            <div className="h-24 skeleton-pulse rounded" />
          ) : (
            <>
              {childFolders.length === 0 && childAttachments.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <FolderOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>{inLicensingFolder
                    ? 'No verified credential files for this employee yet.'
                    : 'This folder is empty. Drag files here or use Upload Files.'}</p>
                </div>
              ) : (
                <>
                  {/* Folders */}
                  {childFolders.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                      {childFolders.map(f => (
                        <div
                          key={f.id}
                          className="group flex items-center gap-2 px-3 py-2 rounded-md border bg-surface hover:bg-slate-50 cursor-pointer"
                          onClick={() => setPath([...path, f.id])}
                          data-testid={`folder-${f.id}`}
                        >
                          <Folder className={cn('w-5 h-5 shrink-0', f.is_reserved ? 'text-amber-600' : 'text-blue-600')} />
                          <span className="flex-1 truncate font-medium" title={f.name}>{f.name}</span>
                          {f.is_reserved && <Lock className="w-3.5 h-3.5 text-amber-600" />}
                          {canManageFolders && !f.is_reserved && (
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                              <button
                                title="Rename"
                                onClick={(e) => { e.stopPropagation(); setRenameTarget(f); setRenameValue(f.name); }}
                                className="p-1 hover:bg-slate-200 rounded"
                                data-testid={`rename-folder-${f.id}`}
                              ><Pencil className="w-3.5 h-3.5" /></button>
                              <button
                                title="Delete"
                                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}
                                className="p-1 hover:bg-red-100 rounded text-red-600"
                                data-testid={`delete-folder-${f.id}`}
                              ><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Files */}
                  {childAttachments.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>File</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Uploaded By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {childAttachments.map(att => {
                          const isLogLink = att.source === 'supervisor_log_entry' && att.supervisor_log_entry_id;
                          return (
                          <TableRow key={att.id} data-testid={`attachment-row-${att.id}`}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />{att.filename}{att.read_only && <Lock className="w-3 h-3 text-amber-600" title="Read-only reference" />}</div>
                              {isLogLink && (
                                <button
                                  type="button"
                                  onClick={() => window.location.assign(`/employees/${employeeId}?tab=supervisor-log#log-${att.supervisor_log_entry_id}`)}
                                  className="text-[11px] text-slate-500 italic hover:underline mt-1 block"
                                  title="Open the linked log entry"
                                  data-testid={`linked-log-${att.id}`}
                                >Linked to: Supervisor Log — {formatDate(att.uploaded_at)?.slice(0, 10) || att.uploaded_at?.slice(0,10)}</button>
                              )}
                            </TableCell>
                            <TableCell><Badge variant="outline" className="capitalize">{(att.source || 'manual').replace(/_/g, ' ')}</Badge></TableCell>
                            <TableCell className="text-sm">{att.uploaded_by_name}</TableCell>
                            <TableCell className="text-sm text-slate-500">{formatDate(att.uploaded_at)}</TableCell>
                            <TableCell className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(att)} data-testid={`download-${att.id}`}><Download className="w-4 h-4" /></Button>
                              {!att.read_only && (att.source === 'manual' || ['admin', 'hr'].includes(user?.role)) && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteAttachment(att)} data-testid={`delete-att-${att.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </CardContent>

      {/* New folder dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
            <DialogDescription>
              {currentFolder ? <>Created inside <b>{currentFolder.name}</b>.</> : 'Created at the root of this employee\u2019s attachments.'}
            </DialogDescription>
          </DialogHeader>
          <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" data-testid="new-folder-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Cancel</Button>
            <Button onClick={handleCreateFolder} data-testid="confirm-create-folder">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid="rename-folder-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={handleRename} data-testid="confirm-rename-folder">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
